import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Book, FileText, Download, Clock, Calendar, Building, Hash,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut
} from 'lucide-react';
import { PhoneMockup } from '../../components/PhoneMockup';
import { QRPageFooter } from '../../components/qr/QRPageFooter';

const SampleDemo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const isDemo = new URLSearchParams(location.search).get('demo') === 'true';
  
  useEffect(() => {
    if (isDemo) {
      window.history.replaceState({}, '', '/qr/demo-sample');
    }
  }, [isDemo]);

  const handleBackToDemos = () => {
    navigate('/');
    setTimeout(() => {
      const demosSection = document.getElementById('demos');
      if (demosSection) {
        const sectionIndex = 7; // Demos is the 8th section (0-indexed)
        window.scrollTo({
          top: sectionIndex * window.innerHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Sample data for a public domain work by Edgar Allan Poe
  const sampleData = {
    title: 'Der Rabe und andere Gedichte',
    author: 'Edgar Allan Poe',
    coverUrl: '/images/der-rabe-und-andere-gedichte-taschenbuch-edgar-allan-poe.webp',
    genre: 'Lyrik / Horror',
    publicationDate: '1845-01-29',
    publisher: 'Max Mustermann Verlag',
    isbn: '978-3-12345-678-9',
    description: 'Eine Sammlung der berühmtesten Gedichte von Edgar Allan Poe, darunter "Der Rabe", eines der bekanntesten Gedichte der amerikanischen Literatur. Diese Ausgabe enthält auch weitere düstere und atmosphärische Werke des Meisters des Grauens.',
    samplePages: 'Erste 3 Kapitel',
    readingTimeMinutes: 15,
    downloadEnabled: true
  };

  const brandingData = {
    companyName: 'Max Mustermann GmbH',
    logoUrl: null,
    primaryColor: '#3B82F6',
    displayType: 'name_only' as const
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          {isDemo && (
            <button
              onClick={handleBackToDemos}
              className="mb-4 text-blue-600 hover:text-blue-700 transition-colors text-sm"
            >
              ← Zurück zu Demos
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Leseprobe Demo</h1>
          <p className="text-gray-600">So sieht Ihre digitale Leseprobe auf dem Smartphone aus</p>
        </div>

        {/* Phone Mockup with Book Sample */}
        <PhoneMockup className="mb-8">
          <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
            {/* Header */}
            <div className="bg-white shadow-sm">
              <div className="container mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold" style={{ color: brandingData.primaryColor }}>
                      {brandingData.companyName}
                    </span>
                  </div>
                  {sampleData.downloadEnabled && (
                    <div className="p-1.5 bg-gray-100 rounded-lg">
                      <Download className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Book Info */}
            <div className="container mx-auto px-4 py-4">
              <div className="bg-white rounded-2xl shadow-xl p-4 mb-4">
                <div className="flex gap-4 mb-4">
                  {sampleData.coverUrl ? (
                    <img
                      src={sampleData.coverUrl}
                      alt={sampleData.title}
                      className="w-24 h-36 rounded-lg shadow-lg object-cover"
                    />
                  ) : (
                    <div className="w-24 h-36 bg-gray-200 rounded-lg shadow-lg flex items-center justify-center">
                      <Book className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <h1 className="text-lg font-bold text-gray-900 mb-1">{sampleData.title}</h1>
                    <p className="text-sm text-gray-700 mb-2">von {sampleData.author}</p>
                    
                    <div className="space-y-1 text-xs">
                      {sampleData.genre && (
                        <div>
                          <span className="text-gray-500">Genre:</span>
                          <span className="ml-1 font-medium">{sampleData.genre}</span>
                        </div>
                      )}
                      {sampleData.publisher && (
                        <div className="flex items-center gap-1">
                          <Building className="w-3 h-3 text-gray-500" />
                          <span className="font-medium">{sampleData.publisher}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {sampleData.description && (
                  <div className="mb-3">
                    <h3 className="font-semibold text-sm mb-1">Beschreibung</h3>
                    <p className="text-xs text-gray-700 leading-relaxed">{sampleData.description}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                  {sampleData.samplePages && (
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{sampleData.samplePages}</span>
                    </div>
                  )}
                  {sampleData.readingTimeMinutes > 0 && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>ca. {sampleData.readingTimeMinutes} Min.</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-2xl shadow-xl p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {sampleData.publicationDate && (
                    <div>
                      <span className="text-gray-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Erschienen:
                      </span>
                      <p className="font-medium mt-0.5">
                        {new Date(sampleData.publicationDate).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  )}
                  {sampleData.isbn && (
                    <div>
                      <span className="text-gray-500 flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        ISBN:
                      </span>
                      <p className="font-medium mt-0.5">{sampleData.isbn}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* PDF Viewer Preview */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-4">
                <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-0.5 bg-gray-200 rounded opacity-50">
                      <ChevronLeft className="w-4 h-4" />
                    </div>
                    <span className="text-xs">Seite 1 von ...</span>
                    <div className="p-0.5 bg-gray-200 rounded">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <ZoomOut className="w-3 h-3" />
                    <span className="text-xs">100%</span>
                    <ZoomIn className="w-3 h-3" />
                  </div>
                </div>
                
                <div className="relative bg-gray-50" style={{ height: '200px' }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">PDF Leseprobe</p>
                      <p className="text-gray-400 text-xs mt-1">Vorschau</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button 
                className="w-full text-white px-4 py-3 rounded-lg font-medium text-sm mb-4"
                style={{ backgroundColor: brandingData.primaryColor }}
              >
                Leseprobe herunterladen
              </button>

              {/* Footer */}
              <QRPageFooter 
                branding={{ primaryColor: brandingData.primaryColor }} 
                className="text-gray-600 text-xs" 
              />
            </div>
          </div>
        </PhoneMockup>

        {/* Demo Info */}
        {isDemo && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold mb-2 text-blue-900">Demo-Hinweis</h3>
              <p className="text-sm text-blue-800 mb-2">
                Dies ist eine Demo einer digitalen Leseprobe mit dem Branding "Max Mustermann GmbH". 
                Die Seite zeigt "Der Rabe und andere Gedichte" von Edgar Allan Poe als Beispiel.
              </p>
              <p className="text-sm text-blue-800">
                Mit BeyondPages können Sie digitale Leseproben Ihrer Bücher bereitstellen, die über 
                QR-Codes erreichbar sind. Optional können Sie E-Mail-Adressen sammeln oder Downloads 
                aktivieren.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleDemo;